import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import type { ChatMessage } from "../components/ChatPanel";

const PAGE = 50;
// Soft cap on messages held in memory. Scrolling up loads older pages until
// this is reached; new messages still arrive and the oldest fall out of the
// window (they stay in the DB up to the server-side retention limit).
const MAX = 500;
const POLL_MS = 4000;

interface ChatResponse {
  messages: ChatMessage[];
  inAlliance?: boolean;
}

// Merge two ascending-by-id lists, dedupe by id, keep the newest MAX.
function merge(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const map = new Map<number, ChatMessage>();
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  const all = Array.from(map.values()).sort((x, y) => x.id - y.id);
  return all.length > MAX ? all.slice(all.length - MAX) : all;
}

// Stateful chat channel: initial last-page load, 4s polling for new messages
// (afterId), and scroll-up history pagination (beforeId). Works for any
// channel sharing the {messages, inAlliance?} response shape.
export function useChatChannel(basePath: string, enabled: boolean) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pending, setPending] = useState(false);
  const [inAlliance, setInAlliance] = useState<boolean | undefined>(undefined);

  const msgsRef = useRef<ChatMessage[]>([]);
  msgsRef.current = messages;
  const inited = useRef(false);

  // Initial load (and reload when the channel becomes active).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    inited.current = false;
    setIsLoading(true);
    api
      .get<ChatResponse>(basePath)
      .then((res) => {
        if (cancelled) return;
        const m = res.data.messages ?? [];
        setMessages(m);
        setInAlliance(res.data.inAlliance);
        setHasMore(m.length >= PAGE);
        setIsLoading(false);
        inited.current = true;
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, enabled]);

  // Poll for new messages.
  useEffect(() => {
    if (!enabled) return;
    const tick = async () => {
      if (!inited.current) return;
      const cur = msgsRef.current;
      const lastId = cur.length ? cur[cur.length - 1].id : 0;
      try {
        const res = await api.get<ChatResponse>(`${basePath}?afterId=${lastId}`);
        setInAlliance(res.data.inAlliance);
        const incoming = res.data.messages ?? [];
        if (incoming.length) setMessages((prev) => merge(prev, incoming));
      } catch {
        // transient network blip — next tick retries
      }
    };
    const h = setInterval(tick, POLL_MS);
    return () => clearInterval(h);
  }, [basePath, enabled]);

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore) return;
    const cur = msgsRef.current;
    if (!cur.length) return;
    if (cur.length >= MAX) {
      setHasMore(false);
      return;
    }
    const oldestId = cur[0].id;
    setLoadingOlder(true);
    try {
      const res = await api.get<ChatResponse>(`${basePath}?beforeId=${oldestId}`);
      const older = res.data.messages ?? [];
      if (older.length) setMessages((prev) => merge(older, prev));
      if (older.length < PAGE) setHasMore(false);
    } catch {
      // ignore; user can scroll again
    } finally {
      setLoadingOlder(false);
    }
  }, [basePath, loadingOlder, hasMore]);

  const send = useCallback(
    (body: string) => {
      setPending(true);
      api
        .post<{ message?: ChatMessage }>(basePath, { body })
        .then((res) => {
          const msg = res.data?.message;
          if (msg) setMessages((prev) => merge(prev, [msg]));
        })
        .finally(() => setPending(false));
    },
    [basePath],
  );

  return { messages, isLoading, loadingOlder, hasMore, pending, inAlliance, loadOlder, send };
}
